import * as React from 'react';
import { useMemo, useCallback } from 'react';
import { css } from '@emotion/core';
import Msg from '../localization/Msg';
import { Str } from '../localization/translationKeys';
import { COLORS } from '../globalStyle/colors';
import Spacer from '../layout/Spacer';
import { useAppState } from '../core/redux/hooks';
import { useMsgText } from '../localization/hooks';


const THUMB_COLOR        = COLORS.WHITE;
const CHECKED_BG_COLOR   = COLORS.BLUE;
const UNCHECKED_BG_COLOR = COLORS.LIGHT_GREY;
const TEXT_COLOR         = COLORS.DARK_GREY;

const styles = {
  label:                 css`
    display: flex;
    flex-direction: row;
    align-items: center;
    
    user-select: none;
    cursor: pointer;
  `,
  hiddenInput:           css`
    opacity: 0;
    width: 0;
    height: 0;
    margin: 0;
    padding: 0;

    /* The input is only visually hidden (never display: none, which would drop it from the keyboard and from screen
       readers), so draw the keyboard focus ring on the visible track instead of on the input itself. */
    &:focus + div > span {
      box-shadow: 0 0 0 2px ${COLORS.WHITE}, 0 0 0 4px ${COLORS.DARK_BLUE};
    }

    &:focus:not(:focus-visible) + div > span {
      box-shadow: none;
    }
  `,
  visibleInputContainer: css`
    position: relative;
    width: 34px;
    height: 20px;
    flex-basis: 34px;
    flex-shrink: 0;
    flex-grow: 0;
  `,
  visibleInput:          css`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    
    border-radius: 45px;
    transition: .4s;
    pointer-events: none;
    
    background-color: ${UNCHECKED_BG_COLOR};

    /* The little thumb circle */ 
    &:before {
      position: absolute;
      left: 2px;
      bottom: 2px;
      width: 16px;
      height: 16px;
      
      border-radius: 50%;
      background-color: ${THUMB_COLOR};
      transition: .4s;
      content: "";
    }
  `,
  checked:               css`
    background-color: ${CHECKED_BG_COLOR};

    /* Moves the little thumb when checked */
    &:before {
      transform: translateX(14px);
    }
  `,
  disabled:              css`
    opacity: .4;
  `,
  onOffMsg:              css`
    font-size: 13px;
    color: ${TEXT_COLOR}
  `,
};


const defaultGetTxt = (isOn: boolean): Str => isOn ? Str.On : Str.Off;


type Props = {
  value: boolean;
  onChange: (newValue: boolean) => void;
  disable?: boolean;
  getTxt?: (isOn: boolean) => Str;
  /**
   * Translation key naming what the toggle controls (e.g. "Email Reminders"). Without it the checkbox's accessible
   * name is just the visible On/Off text, so a screen reader announces "Off, checkbox" with no subject.
   */
  labelTranslation?: string;
  className?: string;
}


/**
 * Toggle: a component that renders a little thumb toggle element looking similar to an iOS toggle.
 */
const Toggle: React.FC<Props> = props => {
  const dir = useAppState(state => state.locale.textDirection);

  const msg      = useMemo(() => (props.getTxt ?? defaultGetTxt)(props.value), [props.getTxt, props.value]);
  const onChange = useCallback((event) => props.onChange(event.target.checked), [props]);

  // aria-label on the input overrides the wrapping label's On/Off text as the accessible name; the checked state
  // still conveys on/off.
  const ariaLabel = useMsgText(props.labelTranslation ?? '');

  return (
    <label dir={dir} className={props.className} css={[styles.label, props.disable && styles.disabled]}>
      <input
        type='checkbox'
        checked={props.value}
        disabled={!!props.disable}
        onChange={onChange}
        aria-label={ariaLabel || undefined}
        css={styles.hiddenInput}
      />
      <div css={styles.visibleInputContainer}>
        <span css={[styles.visibleInput, props.value && styles.checked]} />
      </div>
      <Spacer sizePx={5} />
      <Msg str={msg} css={styles.onOffMsg} />
    </label>
  );
};

export default Toggle;
